import React, {
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core'

import { TimePicker } from '@mantine/dates'

import { useForm } from '@mantine/form'

import Device, { ValveKey } from '@data/device/model/device'

import {
  DayOfWeek,
  ValveScheduleCommand,
  ValveSchedule,
  ValveScheduleCreate,
} from '@data/device/model/valveSchedule'

interface ValveScheduleFormProps {
  opened: boolean

  onClose: () => void

  // Resolves with the schedules that failed to save; an empty array means all succeeded.
  onSave: (schedules: ValveScheduleCreate[]) => Promise<ValveScheduleCreate[]>

  device: Device

  editingSchedule?: ValveSchedule | null

  existingSchedules: ValveSchedule[]

  prefilledDay?: DayOfWeek

  prefilledTime?: string

  // The schedule list is refetching in the background. Submitting now would validate
  // against a list that is about to change, so the submit button waits.
  isRefreshing?: boolean
}

const DAYS_OF_WEEK: DayOfWeek[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

const VALVE_COMMANDS: ValveScheduleCommand[] = ['open', 'close']

const ValveScheduleForm: React.FC<
  ValveScheduleFormProps
> = ({
       opened,
       onClose,
       onSave,
       device,
       editingSchedule,
       existingSchedules,
       prefilledDay,
       prefilledTime,
       isRefreshing = false,
     }) => {
  const valveOptions = useMemo(() => {
    const options: {
      value: ValveKey
      label: string
      disabled?: boolean
    }[] = Object.values(device.valves)
      .filter(
        (valve) =>
          valve.state !== 7,
      )
      .map((valve) => ({
        value: valve.key,
        label: valve.name ? `${valve.key} - ${valve.name}` : valve.key,
      }))

    // A schedule can outlive its valve being marked Not Installed (state 7) or dropped from the
    // device, and the filter above leaves such a valve out. With no matching option the Valve
    // field renders blank, so the scheduled valve is kept as a disabled option that says why.
    const scheduledValveId = editingSchedule?.valveId

    if (scheduledValveId && !options.some((option) => option.value === scheduledValveId)) {
      const valve = device.valves[scheduledValveId]
      const name = valve?.name ? `${scheduledValveId} - ${valve.name}` : scheduledValveId

      options.push({
        value: scheduledValveId,
        label: `${name} (${valve?.state === 7 ? 'Not Installed' : 'Unavailable'})`,
        disabled: true,
      })
    }

    return options
  }, [
    device.valves,
    editingSchedule,
  ])

  const [isSaving, setIsSaving] = useState(false)

  // After a partial failure the form may hold exactly its initial values (only the
  // primary day failed), which would leave Save disabled by the dirty check.
  const [hasFailedSchedules, setHasFailedSchedules] = useState(false)

  const form = useForm({
    initialValues: {
      dayOfWeek: prefilledDay || 'Monday',

      copyDays: [] as DayOfWeek[],

      timeOfDay: prefilledTime || '',

      valveCommand: 'open' as ValveScheduleCommand,

      valveId: (valveOptions[0]?.value || 'V1') as ValveKey,

      description: '',

      active: true,
    },

    validate: {
      timeOfDay: (
        value,
      ) => {
        if (!value) {
          return 'Time required'
        }

        return null
      },

      copyDays: (
        value,
        values,
      ) => {
        const targetDays = [
          values.dayOfWeek,
          ...value,
        ]

        const duplicateDay =
          targetDays.find(
            (day) =>
              existingSchedules.some(
                (
                  schedule,
                ) =>
                  schedule.dayOfWeek ===
                  day &&
                  schedule.valveId ===
                  values.valveId &&
                  schedule.timeOfDay.startsWith(
                    values.timeOfDay,
                  ) &&
                  schedule.id !==
                  editingSchedule?.id,
              ),
          )

        if (duplicateDay) {
          return `Schedule already exists for ${duplicateDay}`
        }

        return null
      },
    },
  })

  // Resets on every open. The modal stays mounted while closed, so without `opened` here,
  // reopening Add after a cancel (same editingSchedule/prefill values) showed the previous
  // session's values — including the failed days left behind by a partial batch failure.
  useEffect(() => {
    if (!opened) {
      return
    }

    if (
      editingSchedule
    ) {
      form.setValues({
        dayOfWeek: editingSchedule.dayOfWeek,

        copyDays: [],

        timeOfDay: editingSchedule.timeOfDay.slice(0, 5),

        valveCommand: editingSchedule.valveCommand,

        valveId: editingSchedule.valveId,

        description: editingSchedule.description || '',

        active: editingSchedule.active,
      })
    } else {
      form.setValues({
        dayOfWeek: prefilledDay || 'Monday',

        copyDays: [],

        timeOfDay: prefilledTime || '',

        valveCommand: 'open',

        valveId: (valveOptions[0]?.value || 'V1') as ValveKey,

        description: '',

        active: true,
      })
    }

    form.resetDirty()
    form.clearErrors()
    setHasFailedSchedules(false)
  }, [
    opened,
    editingSchedule,
    prefilledDay,
    prefilledTime,
    valveOptions,
  ])

  const handleSubmit =
    async (
      values: typeof form.values,
    ) => {
      const targetDays = [
        values.dayOfWeek,
        ...values.copyDays,
      ]

      const schedules: ValveScheduleCreate[] =
        targetDays.map(
          (
            day,
          ) => ({
            dayOfWeek: day,

            timeOfDay: `${values.timeOfDay}:00`,

            valveCommand: values.valveCommand,

            valveId: values.valveId,

            description: values.description || undefined,

            active: values.active,

            dUuid: device.dUuid,
          }),
        )
      setIsSaving(true)

      let failed: ValveScheduleCreate[]

      try {
        failed = await onSave(schedules)
      } finally {
        setIsSaving(false)
      }

      if (failed.length === 0) {
        setHasFailedSchedules(false)
        onClose()

        return
      }

      // Every schedule in a batch shares valve, time and command and differs only by day,
      // so narrowing the days to the failed ones means a retry sends only those.
      form.setValues({
        dayOfWeek: failed[0].dayOfWeek,
        copyDays: failed.slice(1).map((schedule) => schedule.dayOfWeek),
      })
      setHasFailedSchedules(true)
    }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        editingSchedule ? 'Edit Valve Schedule' : 'Add Valve Schedule'
      }
      centered
      size="lg"
    >
      <form
        onSubmit={form.onSubmit(
          handleSubmit,
        )}
      >
        <Stack gap="md">
          <Select
            label="Valve"
            withAsterisk
            data={valveOptions}
            {...form.getInputProps(
              'valveId',
            )}
          />

          <Select
            label="Day of Week"
            withAsterisk
            data={DAYS_OF_WEEK.map(
              (day) => ({
                value: day,
                label: day,
              }),
            )}
            {...form.getInputProps('dayOfWeek')}
          />

          <TimePicker
            label="Time"
            withAsterisk
            {...form.getInputProps('timeOfDay')}
            minutesStep={60}
          />

          <Select
            label="Command"
            withAsterisk
            data={VALVE_COMMANDS.map(
              (command) => ({
                value: command,
                label: command === 'open' ? 'Open Valve' : 'Close Valve',
              }),
            )}
            {...form.getInputProps('valveCommand')}
          />

          <TextInput
            label="Description"
            placeholder="Optional description"
            {...form.getInputProps('description')}
          />

          <Switch
            label="Enabled"
            description="Disabled schedules are kept but not run by the device."
            checked={form.values.active}
            onChange={(event) =>
              form.setFieldValue('active', event.currentTarget.checked)
            }
          />

          {!editingSchedule && (
            <>
              <Divider />

              <Stack gap="xs">
                <Text
                  fw={600}
                  size="sm"
                >
                  Copy To Additional Days
                </Text>

                <Checkbox.Group
                  {...form.getInputProps('copyDays')}
                >
                  <SimpleGrid cols={2}>
                    {DAYS_OF_WEEK.filter(
                      (day) =>
                        day !== form.values.dayOfWeek,
                    ).map(
                      (
                        day,
                      ) => (
                        <Checkbox
                          key={day}
                          value={day}
                          label={day}
                        />
                      ),
                    )}
                  </SimpleGrid>
                </Checkbox.Group>

                <Text
                  size="xs"
                  c="dimmed"
                >
                  Creates matching
                  schedules for the
                  selected days.
                </Text>
              </Stack>
            </>
          )}

          <Group
            justify="flex-end"
            mt="md"
          >
            <Button
              variant="default"
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              loading={isSaving || isRefreshing}
              disabled={isSaving || isRefreshing || (!form.isDirty() && !hasFailedSchedules)}
            >
              {editingSchedule
                ? 'Update Schedule'
                : 'Create Schedule'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}

export default ValveScheduleForm